/**
 * Authentication helpers for making authenticated API requests
 */

import { supabaseClient } from 'src/db/supabase';

// In extension mode the Supabase session lives in the extension, not in this
// app's client. DataContext registers a fallback provider (backed by the
// extension's getAuthToken message) so authenticated APIs like Anni work there.
let fallbackTokenProvider: (() => Promise<string>) | null = null;

export const registerFallbackTokenProvider = (provider: () => Promise<string>): void => {
  fallbackTokenProvider = provider;
};

/**
 * Get the current user's access token from Supabase, falling back to the
 * extension's session when this app has none.
 * @returns Promise resolving to the access token
 * @throws Error if not authenticated or session is invalid
 */
export const getAuthToken = async (): Promise<string> => {
  const {
    data: { session },
  } = await supabaseClient.auth.getSession();

  if (session?.access_token) return session.access_token;

  if (fallbackTokenProvider) {
    try {
      return await fallbackTokenProvider();
    } catch {
      throw new Error('Not authenticated');
    }
  }

  throw new Error('Not authenticated');
};

/**
 * Make an authenticated fetch request with automatic token injection
 * @param input - The request URL or Request object
 * @param init - Optional fetch init options
 * @returns Promise resolving to the Response
 */
export const authenticatedFetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
  const token = await getAuthToken();

  const headers = new Headers(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);

  return fetch(input, {
    ...init,
    headers,
  });
};
